/**
 * Checklist App - Main Entry Point
 *
 * Responsibilities:
 *   1. Boot dispatch: run schema migration, surface migration-failure
 *      and private-browsing warnings, seed the example list on empty
 *      state, and decide initial screen based on session state.
 *   2. Screen navigation via a simple currentScreen variable.
 *   3. Shared primitives: toast system (imported), long-press helper
 *      (imported), a11y announcer + focus management (imported).
 *   4. Online/offline listener wiring.
 *   5. PWA updateSW handoff to Task 10's update banner.
 *
 * Navigation contract:
 *   navigateTo(screen, params) where screen is one of:
 *     'home' | 'editor' | 'runner' | 'summary'
 *   After render, focus moves to the primary <h1>.
 *
 * Modules:
 *   - Screen renderers live in './screens/*' (home, editor, runner,
 *     summary). The Batch 2 './ui/screen-stubs.js' placeholders have
 *     been retired.
 *   - Modal / banner mounts currently come from './ui/modal-stubs.js'
 *     and will be replaced by Batch 4 imports from './components/*'.
 */

import {
  migrate,
  isPrivateBrowsing,
  STORAGE_KEYS,
  safeRead,
  safeWrite
} from './storage.js';
import { seedExampleListIfEmpty } from './lists.js';
import { getSession, clearSession } from './session.js';
import { announce, focusPrimaryHeading } from './a11y.js';
import { showToast, showError } from './ui/toast.js';
import { isStandalone } from './platform.js';

// All four screens now live in ./screens/*; Batch 4's Task 12 retired the
// last screen-stub consumer (Summary) and deleted ./ui/screen-stubs.js.
import { renderHome } from './screens/home.js';
import { renderEditor } from './screens/editor.js';
import { renderRunner } from './screens/runner.js';
import { renderSummary } from './screens/summary.js';
import {
  showResumeModal,
  mountUpdateBanner,
  mountInstallBanner,
  mountPrivateBrowsingBanner,
  mountMigrationFailureBanner,
  setOfflineIndicator
} from './ui/modal-stubs.js';

// ---------------------------------------------------------------------------
// Screen state
// ---------------------------------------------------------------------------

let currentScreen = null;
let currentParams = {};

const SCREEN_RENDERERS = {
  home: renderHome,
  editor: renderEditor,
  runner: renderRunner,
  summary: renderSummary
};

const API = {
  navigateTo,
  announce,
  showToast,
  showError
};

/**
 * Navigate to a new screen and render it.
 * @param {'home'|'editor'|'runner'|'summary'} screen
 * @param {object} [params]
 */
export function navigateTo(screen, params = {}) {
  const renderer = SCREEN_RENDERERS[screen];
  if (!renderer) {
    throw new Error(`Unknown screen: ${screen}`);
  }
  currentScreen = screen;
  currentParams = params;

  const root = document.getElementById('app');
  renderer(root, params, API);

  // Move focus to primary heading for screen-reader and keyboard users.
  focusPrimaryHeading();
}

/**
 * Read-only accessor, useful for tests.
 */
export function getCurrentScreen() {
  return { screen: currentScreen, params: currentParams };
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

/**
 * Run the app boot sequence.
 *
 * @param {{ skipPwa?: boolean }} [opts] skipPwa suppresses SW registration
 *                                        under Vitest (where the
 *                                        virtual:pwa-register module is
 *                                        not available).
 */
export async function boot(opts = {}) {
  // 1. Schema migration.
  const migration = migrate();
  if (!migration.success) {
    mountMigrationFailureBanner();
  }

  // 2. Private-browsing warning.
  if (isPrivateBrowsing()) {
    mountPrivateBrowsingBanner();
  }

  // 3. Seed example list on empty state.
  seedExampleListIfEmpty();

  // 4. Online/offline wiring.
  const updateOnline = () => setOfflineIndicator(!navigator.onLine);
  window.addEventListener('online', updateOnline);
  window.addEventListener('offline', updateOnline);
  updateOnline();

  // 5. Install banner (only if not in standalone mode and not
  //    previously dismissed). iOS Safari never fires
  //    beforeinstallprompt, so we show a manual "Share → Add to Home
  //    Screen" banner right away. Android/Chromium defers a native
  //    prompt via beforeinstallprompt; when that fires we swap the
  //    banner for one with a native "Install" button.
  try {
    const seen = safeRead(STORAGE_KEYS.SEEN_INSTALL_BANNER, false);
    if (!isStandalone() && !seen) {
      const dismissInstallBanner = () => {
        safeWrite(STORAGE_KEYS.SEEN_INSTALL_BANNER, true);
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      };
      const onBeforeInstallPrompt = (e) => {
        e.preventDefault();
        const existing = document.getElementById('install-banner');
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        mountInstallBanner(dismissInstallBanner, { installPromptEvent: e });
      };
      window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      mountInstallBanner(dismissInstallBanner);
    }
  } catch (_e) {
    // Environment without addEventListener (e.g., jsdom).
  }

  // 6. PWA registration (production only).
  if (!opts.skipPwa) {
    await registerPwa();
  }

  // 6b. Visibility gate: when the Runner has been backgrounded for
  //     longer than INTERRUPT_THRESHOLD_MS and then becomes visible
  //     again, show the resume-or-start-over modal rather than
  //     dropping the user mid-item. iOS PWAs often restore in-memory
  //     state after a brief background, so this catches the
  //     "interrupted partway" case that boot() already handles on a
  //     cold launch.
  setupVisibilityGate(opts.interruptThresholdMs);

  // 7. Boot dispatch: decide initial screen from session state.
  const session = getSession();
  if (session && session.completed) {
    navigateTo('summary', {});
    return;
  }
  if (session && !session.completed) {
    showResumeModal({
      listName: session.listName,
      currentIndex: session.currentIndex,
      total: session.items.length,
      onResume: () => navigateTo('runner', {}),
      onStartOver: () => {
        clearSession();
        navigateTo('home', {});
      }
    });
    return;
  }
  navigateTo('home', {});
}

const DEFAULT_INTERRUPT_THRESHOLD_MS = 30_000;

/**
 * Install a visibilitychange listener that shows the resume modal
 * when the user comes back to the Runner after being away for more
 * than `thresholdMs`. Any prior handler installed by a previous
 * boot() call (e.g. across vi.resetModules in tests) is removed so
 * that stale closures do not accumulate on the document.
 */
function setupVisibilityGate(thresholdMs) {
  if (typeof document === 'undefined' ||
      typeof document.addEventListener !== 'function') return;

  const prior = document.__checklistVisibilityHandler;
  if (prior) {
    document.removeEventListener('visibilitychange', prior);
    document.__checklistVisibilityHandler = null;
  }

  const threshold = typeof thresholdMs === 'number'
    ? thresholdMs
    : DEFAULT_INTERRUPT_THRESHOLD_MS;
  let hiddenAt = null;

  const handler = () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    const prev = hiddenAt;
    hiddenAt = null;
    if (prev === null) return;
    if (Date.now() - prev < threshold) return;
    if (currentScreen !== 'runner') return;

    const active = getSession();
    if (!active || active.completed) return;
    if (document.querySelector('[aria-labelledby="resume-heading"]')) return;

    showResumeModal({
      listName: active.listName,
      currentIndex: active.currentIndex,
      total: active.items.length,
      onResume: () => { /* stay on runner */ },
      onStartOver: () => {
        clearSession();
        navigateTo('home', {});
      }
    });
  };

  document.__checklistVisibilityHandler = handler;
  document.addEventListener('visibilitychange', handler);
}

/**
 * Register the service worker via vite-plugin-pwa's registerSW helper
 * and hand the "update available" callback to the update banner.
 * Virtual import; fails silently in test environments.
 */
async function registerPwa() {
  try {
    const { registerSW } = await import('virtual:pwa-register');
    const updateSW = registerSW({
      onNeedRefresh() {
        mountUpdateBanner(() => updateSW(true));
      },
      onOfflineReady() {
        showToast('Ready to work offline', { variant: 'success' });
      }
    });
  } catch (_e) {
    // virtual:pwa-register is not available outside the Vite build
    // (e.g., Vitest). This is expected; no-op.
  }
}

// ---------------------------------------------------------------------------
// No auto-boot from this module.
// ---------------------------------------------------------------------------
// The boot trigger lives in index.html as an inline <script type="module">
// that imports { boot } and calls it after DOMContentLoaded. Keeping boot
// out of the top-level of main.js means Vitest can `await import('../main.js')`
// without firing migrate/seed/listener-registration side effects before the
// test's explicit `await boot({ skipPwa: true })` call.
